/**
 * platform.ts — 封裝 Tauri 桌面與 PWA 瀏覽器的差異。
 * 其餘程式碼一律從這裡 import，不要在各處直接呼叫 @tauri-apps/* 或 window.Notification。
 */

declare const __IS_TAURI__: boolean

export const isTauri = (): boolean => {
  try {
    return typeof __IS_TAURI__ !== 'undefined' && __IS_TAURI__
  } catch {
    return false
  }
}

// ─── 通知 ────────────────────────────────────────────────

export interface NotifyOptions {
  title: string
  body?: string
}

export async function sendNotification(opts: NotifyOptions): Promise<void> {
  if (isTauri()) {
    const { sendNotification: tauriNotify } = await import(
      '@tauri-apps/plugin-notification'
    )
    await tauriNotify({ title: opts.title, body: opts.body ?? '' })
  } else {
    if (Notification.permission === 'granted') {
      new Notification(opts.title, { body: opts.body })
    }
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (isTauri()) {
    const { requestPermission, isPermissionGranted } = await import(
      '@tauri-apps/plugin-notification'
    )
    const already = await isPermissionGranted()
    if (already) return true
    const perm = await requestPermission()
    return perm === 'granted'
  } else {
    const result = await Notification.requestPermission()
    return result === 'granted'
  }
}

// ─── 存檔對話框 ──────────────────────────────────────────

/** 讓使用者選擇儲存路徑（Tauri），或觸發瀏覽器下載（PWA）。 */
export async function saveFile(opts: {
  suggestedName: string
  data: Uint8Array
  mimeType: string
}): Promise<void> {
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog')
    const { writeFile } = await import('@tauri-apps/plugin-fs')
    const path = await save({ defaultPath: opts.suggestedName })
    if (path) {
      await writeFile(path, opts.data)
    }
  } else {
    const blob = new Blob([opts.data.buffer as ArrayBuffer], { type: opts.mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = opts.suggestedName
    a.click()
    URL.revokeObjectURL(url)
  }
}

// ─── 開機自啟（僅 Tauri）────────────────────────────────

export async function setAutostart(enabled: boolean): Promise<void> {
  if (!isTauri()) return
  const { enable, disable } = await import('@tauri-apps/plugin-autostart')
  if (enabled) {
    await enable()
  } else {
    await disable()
  }
}

export async function isAutostartEnabled(): Promise<boolean> {
  if (!isTauri()) return false
  const { isEnabled } = await import('@tauri-apps/plugin-autostart')
  return isEnabled()
}
